import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { SystemSettingService } from '@app/api/systemsettting.service';
import { environment } from 'environments/environment';

declare var $: any;

// Items under 'vision_mission' section type use the rich text editor (Summernote)
// for long-form content formatting and inline image embedding instead of plain text input.

type ImagePosition = 'left' | 'right' | 'top' | 'bottom';

export interface ModalAboutUsData {
  isNew: boolean;
  parentId: string;
  sectionType: string;
  sectionTitle: string;
  lang: string;
  item?: {
    id?: string;
    parent_id?: string;
    type?: string;
    name?: string;
    value?: string;
    description?: string;
    sort_order?: number;
    is_hidden?: boolean;
    page_location?: string;
    language?: string;
  };
}

@Component({
  selector: 'modal-edit-about-us-item',
  templateUrl: './modal-edit-about-us-item.component.html',
  styleUrls: ['./modal-edit-about-us-item.component.scss'],
})
export class ModalEditAboutUsItemComponent implements OnInit {
  isNew: boolean;
  parentId: string;
  sectionType: string;
  sectionTitle: string;
  lang: string;
  selectedFile: File | null = null;
  imagePreview: string | null = null;
  isSaving = false;
  host = environment.backendhost;
  itemId: string | null = null;
  isUploadingInlineImage = false;

  richTextEditorConfig: any = {
    placeholder: 'Nhập nội dung chi tiết...',
    tabsize: 2,
    height: 220,
    dialogsInBody: true,
    focus: false,
    toolbar: [
      ['style', ['bold', 'italic', 'underline', 'strikethrough', 'clear']],
      ['para', ['ul', 'ol', 'paragraph']],
      ['insert', ['aboutUsImage', 'link']],
      ['view', ['codeview']],
    ],
    buttons: {
      aboutUsImage: (context: any) => this.buildInlineImageButton(context),
    },
  };

  itemForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl(''),
    value: new FormControl(''),
    sort_order: new FormControl(1, [Validators.min(0)]),
    is_hidden: new FormControl(false),
  });

  constructor(
    private dialogRef: MatDialogRef<ModalEditAboutUsItemComponent>,
    private toastr: ToastrService,
    private systemService: SystemSettingService,
    @Inject(MAT_DIALOG_DATA) public data: ModalAboutUsData
  ) {
    this.isNew = data.isNew;
    this.parentId = data.parentId;
    this.sectionType = data.sectionType;
    this.sectionTitle = data.sectionTitle;
    this.lang = data.lang;
  }

  ngOnInit(): void {
    if (!this.isNew && this.data && this.data.item) {
      const item = this.data.item;
      this.itemId = item.id || null;
      this.itemForm.patchValue({
        name: item.name || '',
        description: item.description || '',
        value: item.value || '',
        sort_order: item.sort_order != null ? item.sort_order : 1,
        is_hidden: item.is_hidden || false,
      });

      if (this.isImage(item.name, item.value)) {
        this.imagePreview = this.getImageUrl(item.value);
      }
    }

    // Update preview if value changes and is an image URL
    this.itemForm.get('value').valueChanges.subscribe((val) => {
      if (!this.selectedFile && this.isImage(this.itemForm.get('name').value, val)) {
        this.imagePreview = this.getImageUrl(val);
      } else if (!this.selectedFile && !val) {
        this.imagePreview = null;
      }
    });

    this.itemForm.get('name').valueChanges.subscribe((name) => {
      const val = this.itemForm.get('value').value;
      if (!this.selectedFile && this.isImage(name, val)) {
        this.imagePreview = this.getImageUrl(val);
      }
    });
  }


  isRichText(): boolean {
    // Check if current section or item type is 'vision_mission'
    const type = (
      this.sectionType ||
      (this.data && (this.data.sectionType || (this.data.item && this.data.item.type))) ||
      ''
    ).toLowerCase();
    if (type === 'vision_mission') {
      return true;
    }
    // Fallback: check legacy key names for vision and mission
    const nameControl = this.itemForm.get('name');
    const name = ((nameControl && nameControl.value) || (this.data && this.data.item && this.data.item.name) || '').toLowerCase();
    return name === 'vision' || name === 'mission';
  }

  private buildInlineImageButton(context: any): any {
    const ui = $.summernote.ui;
    const positions: { value: ImagePosition; text: string }[] = [
      { value: 'left', text: 'Ảnh bên trái, chữ bên phải' },
      { value: 'right', text: 'Ảnh bên phải, chữ bên trái' },
      { value: 'top', text: 'Ảnh phía trên, chữ phía dưới' },
      { value: 'bottom', text: 'Chữ phía trên, ảnh phía dưới' },
    ];
    return ui.dropdownButton({
      title: '<i class="note-icon-picture"></i>',
      tooltip: 'Chèn hình ảnh',
      items: positions,
      template: (item: { value: ImagePosition; text: string }) => item.text,
      itemClick: (event: Event, item: { value: ImagePosition; text: string }) => {
        event.preventDefault();
        this.pickInlineImage(item.value, context);
      },
    });
  }

  private pickInlineImage(position: ImagePosition, context: any): void {
    if (!this.itemId) {
      this.toastr.warning(
        'Vui lòng lưu thuộc tính này trước, sau đó mở lại để chèn hình ảnh vào nội dung'
      );
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (event: any) => {
      const file = event.target.files && event.target.files[0];
      if (file) {
        this.uploadInlineImage(file, position, context);
      }
    };
    input.click();
  }

  private uploadInlineImage(file: File, position: ImagePosition, context: any): void {
    if (!this.itemId) {
      return;
    }
    this.isUploadingInlineImage = true;
    this.systemService.apiSystemSettingIdImagePut(this.itemId, file).subscribe({
      next: (res) => {
        this.isUploadingInlineImage = false;
        const path =
          (res && res.data && (res.data.image || res.data.value || res.data.image_url || res.data.url)) ||
          (res && (res.image || res.value || res.url || res.path)) ||
          '';
        if (!path) {
          this.toastr.error('Tải ảnh lên thành công nhưng không nhận được đường dẫn ảnh');
          return;
        }
        const fullUrl = this.getImageUrl(path);
        this.insertImageAtPosition(fullUrl, position, context);
      },
      error: () => {
        this.isUploadingInlineImage = false;
        this.toastr.error('Không thể tải ảnh lên, vui lòng thử lại');
      },
    });
  }


  private insertImageAtPosition(url: string, position: ImagePosition, context: any): void {
    const safeUrl = url.replace(/"/g, '&quot;');

    if (position === 'left') {
      context.invoke(
        'editor.pasteHTML',
        `<img src="${safeUrl}" style="float:left;max-width:45%;margin:0 16px 10px 0;">`
      );
    } else if (position === 'right') {
      context.invoke(
        'editor.pasteHTML',
        `<img src="${safeUrl}" style="float:right;max-width:45%;margin:0 0 10px 16px;">`
      );
    } else {
      const margin = position === 'top' ? '0 0 12px' : '12px 0 0';
      const block = `<div style="clear:both;text-align:center;margin:${margin};"><img src="${safeUrl}" style="max-width:100%;"></div>`;
      const $editable = context.layoutInfo.editable;
      if (position === 'top') {
        $editable.prepend(block);
      } else {
        $editable.append(block);
      }
      context.invoke('editor.afterCommand');
    }
  }

  isImage(name?: string, value?: string): boolean {
    // Rich text items handle inline images directly within the editor
    if (this.isRichText()) {
      return false;
    }
    const fieldName = (name || (this.itemForm.get('name') ? this.itemForm.get('name').value : '') || '').toLowerCase();
    if (
      fieldName.indexOf('logo') !== -1 ||
      fieldName.indexOf('image') !== -1 ||
      fieldName.indexOf('background') !== -1 ||
      fieldName.indexOf('img') !== -1 ||
      fieldName.indexOf('banner') !== -1 ||
      fieldName.indexOf('avatar') !== -1 ||
      fieldName.indexOf('icon') !== -1
    ) {
      return true;
    }

    const val = (value || (this.itemForm.get('value') ? this.itemForm.get('value').value : '') || '').trim().toLowerCase();
    if (!val) return false;
    if (val.indexOf('/public/') === 0 || val.indexOf('public/') === 0) return true;
    if (val.indexOf('http://') === 0 || val.indexOf('https://') === 0 || val.indexOf('data:image/') === 0) {
      if (val.match(/\.(png|jpe?g|gif|webp|svg|ico)(\?.*)?$/i)) return true;
    }
    return false;
  }

  getImageUrl(path?: string): string {
    if (!path) return '';
    const trimmed = path.trim();
    if (
      trimmed.indexOf('http://') === 0 ||
      trimmed.indexOf('https://') === 0 ||
      trimmed.indexOf('data:') === 0 ||
      trimmed.indexOf('blob:') === 0
    ) {
      return trimmed;
    }
    if (trimmed.indexOf('./assets/') === 0 || trimmed.indexOf('assets/') === 0) {
      return trimmed;
    }
    const host = (this.host || '').replace(/\/+$/, '');
    const cleanPath = trimmed.indexOf('/') === 0 ? trimmed : '/' + trimmed;
    return host + cleanPath;
  }

  onFileSelected(event: any): void {
    const files = event.target && event.target.files ? event.target.files : null;
    const file = files && files.length > 0 ? files[0] : null;
    if (file) {
      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeSelectedFile(): void {
    this.selectedFile = null;
    const currentVal = this.itemForm.get('value').value;
    if (currentVal) {
      this.imagePreview = this.getImageUrl(currentVal);
    } else {
      this.imagePreview = null;
    }
  }

  onSubmit(): void {
    this.itemForm.markAllAsTouched();
    if (this.itemForm.invalid) {
      this.toastr.error('Vui lòng điền các trường bắt buộc');
      return;
    }

    const formVal = this.itemForm.value;
    const payload: any = {
      parent_id: this.parentId,
      type: this.sectionType,
      page_location: 'about_us',
      name: this.isNew
        ? (formVal.name || '').trim()
        : (this.data && this.data.item && this.data.item.name ? this.data.item.name : (formVal.name || '').trim()),
      value: formVal.value || '',
      description: formVal.description || '',
      sort_order: Number(formVal.sort_order) || 0,
      is_hidden: formVal.is_hidden || false,
      language: this.lang,
    };

    this.isSaving = true;

    if (this.isNew) {
      this.systemService.apiSystemSettingPost(payload).subscribe({
        next: (res) => {
          if (res && res.success && res.data) {
            const newId = res.data.id;
            if (this.selectedFile) {
              this.systemService.apiSystemSettingIdUploadImagePut(newId, this.selectedFile).subscribe({
                next: () => {
                  this.isSaving = false;
                  this.toastr.success('Thêm thuộc tính thành công');
                  this.dialogRef.close({ success: true });
                },
                error: () => {
                  this.isSaving = false;
                  this.toastr.warning('Thêm thành công nhưng tải ảnh thất bại');
                  this.dialogRef.close({ success: true });
                },
              });
            } else {
              this.isSaving = false;
              this.toastr.success('Thêm thuộc tính thành công');
              this.dialogRef.close({ success: true });
            }
          } else {
            this.isSaving = false;
            this.toastr.error('Thêm thuộc tính thất bại');
          }
        },
        error: (err) => {
          this.isSaving = false;
          this.toastr.error('Lỗi khi thêm thuộc tính: ' + (err && err.message ? err.message : ''));
        },
      });
    } else {
      const id = this.data && this.data.item ? this.data.item.id : '';
      payload.id = id;

      this.systemService.apiSystemSettingIdPut(id, payload).subscribe({
        next: (res) => {
          if (res && res.success) {
            if (this.selectedFile) {
              this.systemService.apiSystemSettingIdUploadImagePut(id, this.selectedFile).subscribe({
                next: () => {
                  this.isSaving = false;
                  this.toastr.success('Cập nhật thuộc tính thành công');
                  this.dialogRef.close({ success: true });
                },
                error: () => {
                  this.isSaving = false;
                  this.toastr.warning('Cập nhật thành công nhưng tải ảnh thất bại');
                  this.dialogRef.close({ success: true });
                },
              });
            } else {
              this.isSaving = false;
              this.toastr.success('Cập nhật thuộc tính thành công');
              this.dialogRef.close({ success: true });
            }
          } else {
            this.isSaving = false;
            this.toastr.error('Cập nhật thất bại');
          }
        },
        error: (err) => {
          this.isSaving = false;
          this.toastr.error('Lỗi khi cập nhật: ' + (err && err.message ? err.message : ''));
        },
      });
    }
  }

  onClose(): void {
    this.dialogRef.close({ success: false });
  }
}
